// src/components/AddDocumentMenuWrapper.tsx
import React, { useState, useEffect } from "react";
import "../styles/AddDocumentMenu.css";
import { IconType } from "react-icons";
import { FaQuestion } from "react-icons/fa";
import { Domaine, SousDomaine, SousSousDomaine } from "../types/Domaine";
import { availableIcons } from "../data/AvailableIcons";
import { MenuService } from "../services/MenuService";
import { FenetreService } from "../services/FenetreService";
import { useAuth } from "../context/AuthContext";
import { Fenetre } from "../types/Fenetre";
import { SousFenetre } from "../types/SousFenetre";
import { Indicateur } from "../types/Indicateur";
import CollecteErrorMessage from "../components/CollecteErrorMessage";

// ----- Composant pour afficher les icônes en toute sécurité ----- 
const SafeIcon: React.FC<{ icon?: IconType; className?: string; size?: number }> = ({ icon, className, size }) => {  
  if (!icon) return null;
  const IconComp = icon as React.ComponentType<any>;
  return <IconComp className={className} size={size} />;
};

// Convertir un string en icône
export const stringToIcon = (
  iconName?: string,
  size = 20,
  className = "me-2 text-success"
) => {
  if (!iconName) return null;

  // Cherche l'objet correspondant dans availableIcons
  const found = availableIcons.find(item => item.iconName === iconName);
  if (!found) return null;

  const IconComponent = found.icon as React.ComponentType<any>;

  return <IconComponent size={size} className={className} />;
};

// ----- Composant principal -----
const AddDocumentMenuWrapper: React.FC<{    
  onSelect: (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => void;
  onCreateForm: (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => void;
  action?: string | null;
  onCreateIndicator: (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => void;
  onCreateRapport:(domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => void;
  onClose?: () => void;
}> = ({ onSelect, onCreateForm, onClose, onCreateIndicator, onCreateRapport, action }) => {
  const { api } = useAuth();
  const [selectedDomain, setSelectedDomain] = useState<Domaine | null>(null);
  const [selectedSousDomain, setSelectedSousDomain] = useState<SousDomaine | null>(null);
  const [addingDomain, setAddingDomain] = useState(false);
  const [newDomainLabel, setNewDomainLabel] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<number>(0);
  const [addingSousDomain, setAddingSousDomain] = useState(false);
  const [newSousDomainLabel, setNewSousDomainLabel] = useState("");
  const [addingSousSousDomain, setAddingSousSousDomain] = useState(false);
  const [newSousSousDomainLabel, setNewSousSousDomainLabel] = useState("");
  const [fenetres, setFenetres] = useState<Fenetre[]>([]);
  const [selectedFenetre, setSelectedFenetre] = useState<Fenetre | null>(null);
  const [sousFenetres, setSousFenetres] = useState<SousFenetre[]>([]);
  const [domaines, setDomaines] = useState<Domaine[]>([]);
  const [indicateurs, setIndicateurs] = useState<Indicateur[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const fetchMenus = async () => {
    try {
      const allMenus = await MenuService.getAllMenus(api);

      const mappedDomaines: Domaine[] = allMenus.map(menu => {
        const iconObj = availableIcons.find(i => i.iconName === menu.iconLabel);

        return {
          id: menu.id,
          label: menu.label || "",
          icon: iconObj?.icon || FaQuestion,
          iconName: iconObj?.iconName || "FaQuestion",
          sousDomaines: [],
        };
      });

      setDomaines(mappedDomaines);
    } catch (error: any) {
      console.error("Erreur lors de la récupération des menus :", error.message);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  const { user } = useAuth();

  const hasIndicateur = (fenetreOrSousFenetreLabel?: string) => {
    if (!fenetreOrSousFenetreLabel) return false;
    return indicateurs.some(ind => ind.tableName === fenetreOrSousFenetreLabel);
  };

  // Fonction pour sélectionner une icône
  const handleSelectIcon = (idxIcon: number) => {
    setSelectedIcon(idxIcon);
  };

  const handleSelectDomain = async (domain: Domaine) => {
    setSelectedDomain(domain);
    setSelectedSousDomain(null);
    setAddingSousDomain(false);

    if (!domain.id) {
      console.error("Le domaine sélectionné n’a pas d’ID !");
      return;
    }

    try {
      const fenetres = await FenetreService.getByIdMenu(api, domain.id);
      setFenetres(fenetres);
    } catch (error) {
      console.error("Erreur lors du chargement des fenêtres :", error);
    }
  };

  const handleSelectFenetre = async (fenetre: Fenetre) => {
    setSelectedFenetre(fenetre);
    setSelectedSousDomain({ label: fenetre.nom?fenetre.nom:"", id: fenetre.id });
    setAddingSousSousDomain(false);

    if (!fenetre.id) {
      console.error("La fenêtre sélectionnée n’a pas d’ID !");
      return;
    }

    try {
      const fetchedSousFenetres = await FenetreService.getByIdFenetre(api, fenetre.id);
      setSousFenetres(fetchedSousFenetres);
    } catch (error) {
      console.error("Erreur lors du chargement des sous-fenêtres :", error);
      setSousFenetres([]);
    }
  };

  const handleBackFromSousDomain = () => {
    setSelectedDomain(null);
    setSelectedSousDomain(null);
  };

  const handleBackFromSousSousDomain = () => {
    setSelectedSousDomain(null);
  };

  const handleSelectSousSousDomain = (sousSousDomain: SousSousDomaine) => {
    if (selectedDomain && selectedSousDomain) {
      selectedDomain.icon=undefined;
      onSelect(selectedDomain, selectedSousDomain, sousSousDomain);
      setSelectedDomain(null);
      setSelectedSousDomain(null);
      onClose?.();
    }
  };

  const handleCreateFormFromSousDomain = () => {
    if (selectedDomain && selectedSousDomain) {
      onCreateForm(selectedDomain, selectedSousDomain);
      setSelectedDomain(null);
      setSelectedSousDomain(null);
      onClose?.();
    }
  };

  const handleCreateFormFromSousSousDomain = (sousSousDomain: SousSousDomaine) => {
    if (selectedDomain && selectedSousDomain) {
      onCreateForm(selectedDomain, selectedSousDomain, sousSousDomain);
      setSelectedDomain(null);
      setSelectedSousDomain(null);
      onClose?.();
    }
  };  

  const handleAddDomain = async () => {
    if (!newDomainLabel.trim()) return;

    const selectedIconObj = availableIcons[selectedIcon];

    const newDomain: Domaine = {
      label: newDomainLabel,
      icon: selectedIconObj?.icon,
      iconName: selectedIconObj?.iconName,
      sousDomaines: [],
    };

    try {
      await MenuService.insertMenu(api, {
        label: newDomain.label,
        iconLabel: newDomain.iconName,
      });

      await fetchMenus();

      setNewDomainLabel("");
      setAddingDomain(false);

    } catch (error: any) {
      console.error("Erreur lors de l'insertion du domaine :", error);

      if (error.message?.includes("menu.name.not-unique")) {
        setErrorMessage("Ce nom de domaine existe déjà.");
      } else if (error.message === "SERVER_ERROR") {
        setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setErrorMessage("Erreur lors de l'ajout du domaine.");
      }

      setTimeout(() => {
        setErrorMessage("");
      }, 1500);
    }
  };

  const handleAddSousDomain = async () => {
    if (!newSousDomainLabel.trim() || !selectedDomain) return;

    const newFenetre: Fenetre = {
      titre: "",
      nom: newSousDomainLabel,
      tbl_name: newSousDomainLabel,
      id_menu: selectedDomain.id!,
      icon_label: selectedDomain.iconName || "",
    };

    try {
      const insertedFenetre = await FenetreService.addFenetre(api, newFenetre);
      const updatedFenetres = await FenetreService.getByIdMenu(api, selectedDomain.id!);
      setFenetres(updatedFenetres);

    } catch (error: any) {
      console.error("Erreur lors de l'ajout de la fenêtre :", error.message);

      if (error.message?.includes("fenetre.menu.name-not-unique")) {
        setErrorMessage("Ce nom de fenêtre existe déjà pour ce domaine.");
      } else if (error.message === "SERVER_ERROR") {
        setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setErrorMessage("Erreur lors de l'ajout de la fenêtre.");
      }

      setTimeout(() => {
        setErrorMessage("");
      }, 1500);
    }

    setNewSousDomainLabel("");
    setAddingSousDomain(false);
  };

  const handleAddSousSousDomain = async () => {
    if (
      !newSousSousDomainLabel.trim() ||
      !selectedDomain ||
      !selectedSousDomain ||
      !selectedFenetre
    ) return;

    try {
      const insertedSousFenetre = await FenetreService.addSousFenetre(api, {
        nomSousFenetre: newSousSousDomainLabel,
        tblName: newSousSousDomainLabel,
        titre: "",
        fenetre: { id: selectedFenetre.id! },
      });      

      if (!selectedFenetre?.id) {
        console.error("La fenêtre sélectionnée n’a pas d’ID !");
        return;
      }

      const fetchedSousFenetres = await FenetreService.getByIdFenetre(api, selectedFenetre.id);
      setSousFenetres(fetchedSousFenetres);

      const newSousSous: SousSousDomaine = {
        label: newSousSousDomainLabel,
        id: insertedSousFenetre.idSousFenetre,
      };

      setSelectedSousDomain({
        ...selectedSousDomain,
        sousSousDomaines: [
          ...(selectedSousDomain.sousSousDomaines || []),
          newSousSous
        ],
      });

    } catch (error: any) {
      console.error("Erreur lors de l'ajout d’un sous-sous-domaine :", error);

      if (error.message?.includes("sous_fenetre.fenetre.name-not-unique")) {
        setErrorMessage("Ce sous-sous-domaine existe déjà pour cette fenêtre.");
      } else if (error.message === "SERVER_ERROR") {
        setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setErrorMessage("Erreur lors de l'ajout du sous-sous-domaine.");
      }

      setTimeout(() => setErrorMessage(""), 1500);
    }

    setNewSousSousDomainLabel("");
    setAddingSousSousDomain(false);
  };

  const handleOverlayClick = () => {
    setSelectedDomain(null);
    setSelectedSousDomain(null);
    setAddingDomain(false);
    setAddingSousDomain(false);
    setAddingSousSousDomain(false);
    onClose?.();
  };

  // Fonction pour générer le breadcrumb
  const getBreadcrumb = () => {
    let path = ["Documents"];
    if (selectedDomain) path.push(selectedDomain.label);
    if (selectedSousDomain) path.push(selectedSousDomain.label);
    return path;
  };

  return (
    <div className="add-doc-overlay" onClick={handleOverlayClick}>
      <CollecteErrorMessage
        message={errorMessage}
        visible={!!errorMessage}
        onClose={() => setErrorMessage("")}
      />

      <div className="add-doc-modal" onClick={e => e.stopPropagation()}>
        {/* Breadcrumb */}
        <div className="add-doc-breadcrumb mb-3 text-muted small">
          {getBreadcrumb().map((item, idx) => (
            <span key={idx}>
              {idx > 0 && " > "}
              <span className={idx === getBreadcrumb().length - 1 ? "fw-bold" : ""}>{item}</span>
            </span>
          ))}
        </div>

        <div className="text-center mb-4">
          <i className="bi bi-file-earmark-plus add-doc-icon text-success fs-2"></i>
          <h5 className="fw-bold text-success mt-2">
            {selectedSousDomain ? "Sous-fenêtres" : selectedDomain ? "Fenêtres" : "Menus "}
          </h5>
          <p className="text-muted small">
            {selectedSousDomain ? "Sélectionner un sous-fenêtre ou créer un formulaire" : selectedDomain ? "Sélectionner ou ajouter une fenêtre" : "Sélectionner le menu adéquat"}
          </p>
        </div>

        <div className="add-doc-list">
          {/* Niveau 1: Domaines */}
          {!selectedDomain && (
            <>
            {domaines.length === 0 && (
              <div className="text-center text-muted mb-3">
                Aucun menu actif
              </div>
            )}
              {domaines.map((d, i) => (
                <button key={i} className="add-doc-item" onClick={() => handleSelectDomain(d)}>
                  <div className="d-flex align-items-center">
                    <SafeIcon icon={d.icon} className="text-success fs-5 me-3" size={20} />
                    <span>{d.label}</span>
                  </div>
                  <i className="bi bi-arrow-right-short fs-4 text-muted"></i>
                </button>
              ))}

              {user?.roleRank === 5 && (
                <button
                  className="add-doc-item text-center text-success fw-bold mt-2"
                  onClick={() => setAddingDomain(true)}
                >
                  + Ajouter un menu
                </button>
              )}

              {addingDomain && (
                <div className="mb-3 mt-2">
                  <input
                    type="text"
                    className="form-control mb-3"
                    placeholder="Nom du nouveau domaine"
                    value={newDomainLabel}
                    onChange={e => setNewDomainLabel(e.target.value)}
                  />

                  <div className="icon-grid mb-3" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {availableIcons.map(({ name, icon, iconName }, idx) => (
                      <div
                        key={idx}
                        className={`icon-choice p-2 border rounded text-center ${selectedIcon === idx ? "bg-success text-white" : ""}`}
                        style={{ cursor: "pointer", width: 60 }}
                        onClick={() => handleSelectIcon(idx)}
                      >
                        <SafeIcon icon={icon} size={28} />
                        <small style={{ fontSize: "0.7rem" }}>{name}</small>
                      </div>
                    ))}
                  </div>

                  <button className="btn btn-success w-100" onClick={handleAddDomain}>
                    Ajouter le menu
                  </button>
                </div>
              )}
            </>
          )}
            {/* Niveau 2: Sous-Domaines */}
            {selectedDomain && !selectedSousDomain && (
              <>
                <button className="add-doc-item text-start text-muted mb-3" onClick={handleBackFromSousDomain}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux menus
                </button>

                {fenetres.map((fen, i) => (
                  <button
                    key={i}
                    className="add-doc-item"
                    onClick={() => {
                      if (action === "createForm") {
                        if (fen.titre && fen.titre.trim() !== "") {
                          alert("Il y a déjà un formulaire associé");
                          return;
                        }else{

                        }
                      }
                      else if(action === "manual" || action ==="import"){
                        if (fen.titre && fen.titre.trim() !== "") {
                          selectedDomain.icon=undefined;
                          onSelect(selectedDomain,{ label: fen.nom?fen.nom:"", id: fen.id });
                          return;
                        }
                      }
                      else if (action === "createIndicator") {
                        if (fen.titre && fen.titre.trim() !== "") {
                          onCreateIndicator(selectedDomain, { label: fen.nom ? fen.nom : "", id: fen.id });
                          return;
                        }
                      } 
                      else if (action === "createRapport") {
                        const hasSousFenetres = sousFenetres.some(sf => sf.idSousFenetre=== fen.id);
                        if ((fen.titre && fen.titre.trim() !== "")) {
                          onCreateRapport(selectedDomain, { label: fen.nom ?? "", id: fen.id });
                          return;
                        }
                      }
                      handleSelectFenetre(fen);
                    }}
                  >
                    <span>{fen.nom}</span>
                    <i className="bi bi-arrow-right-short fs-4 text-muted"></i>

                    {action === "createRapport" && hasIndicateur(fen.nom) && (
                      <button
                        className="add-doc-item text-center text-success fw-bold mt-2 d-flex align-items-center justify-content-center"
                        onClick={() => onCreateRapport(selectedDomain, { label: fen.nom ? fen.nom : "", id: fen.id  })}
                      >
                        <i className="bi bi-plus-circle me-2 fs-5"></i>
                        Créer un rapport
                      </button>
                    )}
                  </button>
                  
                ))}

                {/* Bouton “Ajouter une fenêtre” */}
                {user?.roleRank === 5 && (
                  <button className="add-doc-item text-center text-success fw-bold mt-2" onClick={() => setAddingSousDomain(true)}>
                    + Ajouter une fenêtre
                  </button>
                )}

                {addingSousDomain && (
                  <div className="mb-3 mt-2">
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Nom de la nouvelle fenêtre"
                      value={newSousDomainLabel}
                      onChange={e => setNewSousDomainLabel(e.target.value)}
                    />
                    <button className="btn btn-success w-100" onClick={handleAddSousDomain}>
                      Ajouter la fenêtre
                    </button>
                  </div>
                )}
              </>
              
            )}

          {/* Niveau 3: Sous-Sous-Domaines */}
          {selectedDomain && selectedSousDomain && (
            <>
              <button className="add-doc-item text-start text-muted mb-3" onClick={handleBackFromSousSousDomain}>
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux fenêtres
              </button>

              {sousFenetres.length === 0 && (
                <div className="text-center text-muted mb-3">
                  Aucune sous-fenêtre active
                </div>
              )}

              {sousFenetres.map((sf, i) => (
                <button
                  key={i}
                  className="add-doc-item"
                  onClick={() => {
                    if (action === "createForm") {
                      handleCreateFormFromSousSousDomain({ label: sf.nomSousFenetre, id: sf.idSousFenetre });
                    }
                    else if (action === "createIndicator") {
                      onCreateIndicator(selectedDomain, selectedSousDomain, { label: sf.nomSousFenetre, id: sf.idSousFenetre });
                    }
                    else if (action === "createRapport") {
                      onCreateRapport(
                        selectedDomain,
                        selectedSousDomain,
                        { label: sf.nomSousFenetre, id: sf.idSousFenetre }
                      );
                    }
                    else {
                      handleSelectSousSousDomain({ label: sf.nomSousFenetre, id: sf.idSousFenetre });
                    }
                  }}
                >
                  <span>{sf.nomSousFenetre}</span>
                  <i className="bi bi-arrow-right-short fs-4 text-muted"></i>
                </button>
              ))}

              {/* Bouton “Créer un formulaire” si aucune sous-fenêtre */}
              {sousFenetres.length === 0 && action !== undefined && (
                <button
                  className="add-doc-item text-center text-success fw-bold mt-2 d-flex align-items-center justify-content-center"
                  onClick={handleCreateFormFromSousDomain}
                >
                  <i className="bi bi-plus-circle me-2 fs-5"></i>
                  Créer un formulaire
                </button>
              )}

              {/* Bouton “Créer un indicateur” */}
              {action === "createIndicator" && (
                <button
                  className="add-doc-item text-center text-success fw-bold mt-2 d-flex align-items-center justify-content-center"
                  onClick={() => onCreateIndicator(selectedDomain, selectedSousDomain)}
                >
                  <i className="bi bi-plus-circle me-2 fs-5"></i>
                  Créer un indicateur
                </button>
              )}
              
              {user?.roleRank === 5 && (
                <button className="add-doc-item text-center text-success fw-bold mt-2" onClick={() => setAddingSousSousDomain(true)}>
                  + Ajouter une sous-fenêtre
                </button>
              )}

              {addingSousSousDomain && (
                <div className="mb-3 mt-2">
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Nom du nouveau écran"
                    value={newSousSousDomainLabel}
                    onChange={e => setNewSousSousDomainLabel(e.target.value)}
                  />
                  <button className="btn btn-success w-100" onClick={handleAddSousSousDomain}>
                    Ajouter la sous-fenêtre
                  </button>
                </div>
    
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDocumentMenuWrapper;
